import React, { useState, useEffect } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import { useAuth } from "../../Common/Configurations/AuthContext";
import DeleteAlert from "../../Common/Layout/DeleteAlert";
import moment from "moment";
import Select, { components } from "react-select";
import { toast } from 'react-toastify';
import "../Package/Package.css";     // Ensure correct path
import "../Package/PackageForm.css"; // Ensure correct path

// --- React Select Custom Option ---
const Option = (props) => {
  return (
    <components.Option {...props}>
      <input
        type="checkbox"
        checked={props.isSelected}
        onChange={() => null}
      />{" "}
      <label className="ms-2 mb-0">{props.label}</label>
    </components.Option>
  );
};

const HolidayItinerary = ({ holidayId, packageDetails, onUpdate }) => {
  const initialFieldValues = {
    packageItineraryId: "", // Will generate UUID for local identification
    title: "",
    date: "",
    location: "",
    description: "",
    meals: "Breakfast", // Default
    note: "",
  };

  const { user } = useAuth();
  const [btnLoading, setBtnLoading] = useState(false);
  const [lists, setLists] = useState([]);
  const [values, setValues] = useState(initialFieldValues);
  const [errors, setErrors] = useState({});
  const [isFormVisible, setIsFormVisible] = useState(false);
  
  // Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const mealOptions = [
    { value: "Breakfast", label: "Breakfast" },
    { value: "Lunch", label: "Lunch" },
    { value: "Dinner", label: "Dinner" },
    { value: "Snacks", label: "Snacks" }
  ];

  // 1. Load and Parse Data from Prop
  useEffect(() => {
    if (packageDetails && packageDetails.holidayItinerary) {
        try {
            const parsed = typeof packageDetails.holidayItinerary === 'string'
                ? JSON.parse(packageDetails.holidayItinerary)
                : packageDetails.holidayItinerary;

            // Map keys to ensure frontend camelCase consistency
            const mapped = parsed.map((item, index) => ({
                packageItineraryId: item.packageItineraryId || item.PackageItineraryId || `temp-${index}`,
                title: item.title || item.Title,
                date: item.date || item.Date,
                location: item.location || item.Location,
                description: item.description || item.Description,
                meals: item.meals || item.Meals,
                note: item.note || item.Note
            }));
            
            // Sort by date if available
            mapped.sort((a, b) => new Date(a.date) - new Date(b.date));
            setLists(mapped);
        } catch (e) {
            console.error("JSON Parse Error", e);
            setLists([]);
        }
    } else {
        setLists([]);
    }
  }, [packageDetails]);

  // --- CORE SAVE LOGIC (Updates Holiday JSON) ---
  const saveHolidayData = async (updatedList) => {
      const formData = new FormData();
      formData.append("holidayId", holidayId);
      // Serialize the array to JSON
      formData.append("holidayItinerary", JSON.stringify(updatedList));
      
      if(user?.id) formData.append("userId", user.id);

      try {
          await apiService.update(config.UPDATEHOLIDAY, formData);
          if(onUpdate) {
              onUpdate(); 
          } else {
              setLists(updatedList); // Fallback if no parent refresh
          }
          return true;
      } catch (error) {
          console.error(error);
          toast.error("Failed to update itinerary");
          return false;
      }
  };

  // --- HANDLERS ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setBtnLoading(true);

      // 1. Determine ID (New vs Edit)
      const currentId = values.packageItineraryId || crypto.randomUUID();
      const newItem = { ...values, packageItineraryId: currentId };

      // 2. Update Local Array
      let newList = [...lists];
      const index = newList.findIndex(x => x.packageItineraryId === currentId);

      if (index !== -1) {
          newList[index] = newItem; // Edit
      } else {
          newList.push(newItem); // Add
      }

      const success = await saveHolidayData(newList);
      if(success) {
          handleCancel(); // Reset form
      }
      setBtnLoading(false);
    }
  };

  const handleDelete = async () => {
    if (itemToDelete) {
      const newList = lists.filter(x => x.packageItineraryId !== itemToDelete);
      const success = await saveHolidayData(newList);
      if(success) 
      handleCloseDeleteModal();
    }
  };

  // --- UI Helpers ---
  const handleAddNewClick = () => { resetForm(); setIsFormVisible(true); };
  
  const handleCancel = () => { resetForm(); setIsFormVisible(false); };
  
  const showEditDetails = (data) => {
    // Format date for input field
    const formattedData = {
      ...data,
      date: data.date ? moment(data.date).format('YYYY-MM-DD') : "", // Using Date input, removed time for simplicity in itinerary usually
    };
    setValues(formattedData);
    setIsFormVisible(true);
  };

  const resetForm = () => {
    setValues(initialFieldValues);
    setBtnLoading(false);
    setErrors({});
  };

  const validate = () => {
    let temp = {};
    temp.title = values.title.trim() !== "";
    temp.location = values.location.trim() !== "";
    temp.date = values.date !== "";
    setErrors(temp);
    return Object.values(temp).every(x => x === true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });
  };

  const handleOpenDeleteModal = (id) => { setItemToDelete(id); setShowDeleteModal(true); };
  const handleCloseDeleteModal = () => { setShowDeleteModal(false); setItemToDelete(null); };
  const applyErrorClass = (field) => (errors[field] === false ? " form-control-danger" : "");

  return (
    <div className="PackageItinerary-wrapper">
      <div className="card PackageItinerary-card">
        <div className="card-body">
          <div className="PackageItinerary-header">
            <h4 className="header-title">Day-by-Day Itinerary</h4>
            {!isFormVisible && (
              <button
                type="button"
                className="btn btn-primary PackageItinerary-btn-gradient"
                onClick={handleAddNewClick}
              >
                <i className="ri-add-line me-1"></i> Add Day
              </button>
            )}
          </div>

          {isFormVisible && (
            <div className="PackageItinerary-form-container">
              <h5 className="form-title">
                {values.packageItineraryId ? "Edit Itinerary Day" : "Add Itinerary Day"}
              </h5>
              <form onSubmit={handleSubmit} autoComplete="off">
                <div className="row">
                   <div className="col-lg-6 mb-3">
                    <label className="form-label d-flex align-items-center"><i className="ri-flag-line me-2"></i>Title*</label>
                    <input type="text" name="title" value={values.title} onChange={handleInputChange} className={`form-control ${applyErrorClass("title")}`} placeholder="Visit Burj Khalifa" />
                    {errors.title === false && <div className="validationerror">Please enter a title.</div>}
                  </div>
                   <div className="col-lg-6 mb-3">
                    <label className="form-label d-flex align-items-center"><i className="ri-map-pin-line me-2"></i>Location*</label>
                    <input type="text" name="location" value={values.location} onChange={handleInputChange} className={`form-control ${applyErrorClass("location")}`} placeholder="Dubai" />
                    {errors.location === false && <div className="validationerror">Please enter a location.</div>}
                  </div>

                  <div className="col-lg-12 mb-3">
                    <label className="form-label d-flex align-items-center"><i className="ri-file-text-line me-2"></i>Description</label>
                    <textarea name="description" value={values.description} onChange={handleInputChange} className="form-control" placeholder="Describe the day's activities..." rows="3"></textarea>
                  </div>
                  
                  <div className="col-lg-3 mb-3">
                    <label className="form-label d-flex align-items-center"><i className="ri-calendar-event-line me-2"></i>Date</label>
                    <input type="date" name="date" value={values.date} onChange={handleInputChange} className={"form-control" + applyErrorClass("date")} />
                     {errors.date === false && <div className="validationerror">Please select a date.</div>}
                  </div>
                  
                  <div className="col-lg-6 mb-3">
                    <label className="form-label d-flex align-items-center">
                      <i className="ri-restaurant-2-line me-2"></i>Meals
                    </label>
                    <Select
                      isMulti
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      components={{ Option }}
                      options={mealOptions}
                      value={mealOptions.filter((option) =>
                        values.meals?.split(",").includes(option.value)
                      )}
                      onChange={(selected) =>
                        setValues({
                          ...values,
                          meals: selected.map((opt) => opt.value).join(","),
                        })
                      }
                      placeholder="Select Meals"
                      classNamePrefix="react-select"
                    />
                  </div>
                  
                  <div className="col-lg-12 mb-3">
                    <label className="form-label d-flex align-items-center"><i className="ri-sticky-note-line me-2"></i>Notes</label>
                    <textarea name="note" value={values.note} onChange={handleInputChange} className="form-control" placeholder="Add any extra notes or tips..." rows="2"></textarea>
                  </div>
                </div>
                
                <div className="d-flex justify-content-end gap-2 mt-2">
                  <button type="button" className="btn btn-light" onClick={handleCancel}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={btnLoading}>
                    {btnLoading ? "Saving..." : (values.packageItineraryId ? "Update" : "Submit")}
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="mt-4">
            {lists.length > 0 ? (
              <div className="PackageItinerary-list">
                {lists.map((v, i) => (
                  <div key={v.packageItineraryId || i} className="PackageItinerary-list-item">
                    <div className="PackageItinerary-item-icon">
                      <i className="ri-route-line"></i>
                    </div>
                    <div className="PackageItinerary-item-content">
                      <div className="PackageItinerary-item-header">
                        <h6 className="PackageItinerary-item-title">{v.title}</h6>
                        <div className="PackageItinerary-item-meta">
                          <i className="ri-calendar-todo-line"></i>
                          {moment(v.date).format("ddd, DD MMM YYYY")}
                        </div>
                      </div>
                      <p className="PackageItinerary-item-description">{v.description}</p>
                      <div className="PackageItinerary-item-footer">
                        <div className="PackageItinerary-item-location">
                          <i className="ri-map-pin-2-fill"></i>
                          {v.location}
                        </div>
                        {v.meals && (
                          <div className="PackageItinerary-item-meals">
                            <i className="ri-restaurant-2-line"></i>
                            {v.meals.split(",").join(", ")}
                          </div>
                        )}
                      </div>
                      {v.note && (
                        <div className="PackageItinerary-item-note">
                          <i className="ri-information-line"></i> {v.note}
                        </div>
                      )}
                    </div>
                    <div className="PackageItinerary-item-actions">
                      <button className="PackageItinerary-action-btn edit" onClick={() => showEditDetails(v)}><i className="ri-pencil-fill" /></button>
                      <button className="PackageItinerary-action-btn delete" onClick={() => handleOpenDeleteModal(v.packageItineraryId)}><i className="ri-delete-bin-fill" /></button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="PackageItinerary-empty-state">
                <i className="ri-map-2-line empty-icon"></i>
                <h5>Your Itinerary is Empty</h5>
                <p>Start building the journey by adding the first day's plan.</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <DeleteAlert show={showDeleteModal} handleClose={handleCloseDeleteModal} handleDelete={handleDelete} />
    </div>
  );
};

export default HolidayItinerary;